import { Input, Stack } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";

import {
  CampaignCriteriaKind,
  createCampaignCriteria,
} from "../api/campaign-criteria";
import { startKeywordAgent } from "../api/keyword-agent";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { brand } from "../lib/brand";
import { Button } from "./button";
import {
  CampaignCriteriaFields,
  CampaignCriteriaFormData,
  EMPTY_CRITERIA_FORM,
  campaignCriteriaSchema,
  formValuesToRequest,
} from "./campaign-criteria-fields";
import { CampaignCriteriaPresetPicker } from "./campaign-criteria-preset-picker";
import { Checkbox } from "./checkbox";
import { ChoiceCard } from "./choice-card";
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "./dialog";
import { Field } from "./field";
import { toaster } from "./toaster";

type Step = "choice" | "preset" | "criteria";
type Source = "fill-in" | "preset";

interface StartKeywordAgentDialogProps {
  isOpen: boolean;
  onOpenChange: (details: { open: boolean }) => void;
  onSuccess: () => void;
  workspaceSlug: string;
  customerId: string;
  loginCustomerId: string | null;
  campaignId: number;
  adGroupId: number;
  adGroupName: string;
}

export function StartKeywordAgentDialog({
  adGroupId,
  adGroupName,
  campaignId,
  customerId,
  isOpen,
  loginCustomerId,
  onOpenChange,
  onSuccess,
  workspaceSlug,
}: StartKeywordAgentDialogProps) {
  const [step, setStep] = React.useState<Step>("choice");
  const [source, setSource] = React.useState<Source | null>(null);
  const [selectedPresetId, setSelectedPresetId] = React.useState<number | null>(
    null,
  );
  const [saveAsPreset, setSaveAsPreset] = React.useState(false);
  const [presetName, setPresetName] = React.useState("");
  const [isWorking, withWorking] = useLoading();
  const withToaster = useToaster();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CampaignCriteriaFormData>({
    resolver: zodResolver(campaignCriteriaSchema),
    defaultValues: EMPTY_CRITERIA_FORM,
  });

  // Closing clears the wizard, so reopening starts at step one rather
  // than wherever it was abandoned.
  const handleOpenChange = (details: { open: boolean }) => {
    if (!details.open) {
      setStep("choice");
      setSource(null);
      setSelectedPresetId(null);
      setSaveAsPreset(false);
      setPresetName("");
      reset(EMPTY_CRITERIA_FORM);
    }
    onOpenChange(details);
  };

  const close = () => handleOpenChange({ open: false });

  const start = async (campaignCriteriaId: number) => {
    await startKeywordAgent(
      workspaceSlug,
      customerId,
      loginCustomerId,
      campaignId,
      adGroupId,
      campaignCriteriaId,
    );
    toaster.create({
      type: "success",
      title: `The keyword agent is on`,
      description: `${brand.productName} will read this ad group's search terms and report back within a day.`,
    });
    close();
    onSuccess();
  };

  const next = () => setStep(source === "preset" ? "preset" : "criteria");

  // Applying a preset points the agent straight at it, so later edits to
  // the preset reach this ad group too.
  const startFromPreset = async () => {
    if (!selectedPresetId) return;
    await withWorking(async () => {
      await withToaster(() => start(selectedPresetId));
    });
  };

  const startFromAnswers = handleSubmit(async (data) => {
    await withWorking(async () => {
      await withToaster(async () => {
        const criteria = await createCampaignCriteria(workspaceSlug, {
          ...formValuesToRequest(data),
          // The form asks four questions, none of them a name; a preset
          // needs one to be picked from a list later.
          name:
            saveAsPreset && presetName.trim() ? presetName.trim() : adGroupName,
          kind: saveAsPreset
            ? CampaignCriteriaKind.PRESET
            : CampaignCriteriaKind.EPHEMERAL,
        });
        await start(criteria.id);
      });
    });
  });

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange} size="lg">
      <DialogContent>
        <DialogCloseTrigger />
        {step === "choice" && (
          <>
            <DialogHeader>
              <Stack gap="1">
                <DialogTitle>Activate Keyword Agent</DialogTitle>
                <DialogDescription>
                  Tell {brand.productName} who this ad group is for so it can
                  evaluate your search terms.
                </DialogDescription>
              </Stack>
            </DialogHeader>
            <DialogBody>
              <Stack gap="2">
                <ChoiceCard
                  title="Fill in your answers"
                  description="Write criteria just for this ad group."
                  selected={source === "fill-in"}
                  onSelect={() => setSource("fill-in")}
                />
                <ChoiceCard
                  title="Use a preset"
                  description="Apply criteria you've saved before. Editing the preset later changes every ad group using it."
                  selected={source === "preset"}
                  onSelect={() => setSource("preset")}
                />
              </Stack>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button variant="solid" disabled={!source} onClick={next}>
                Next
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "preset" && (
          <>
            <DialogHeader>
              <Stack gap="1">
                <DialogTitle>Select from preset</DialogTitle>
                <DialogDescription>
                  This ad group will use the preset, including any changes you
                  make to it later.
                </DialogDescription>
              </Stack>
            </DialogHeader>
            <DialogBody>
              <CampaignCriteriaPresetPicker
                workspaceSlug={workspaceSlug}
                selectedId={selectedPresetId}
                onSelectionChange={(preset) =>
                  setSelectedPresetId(preset?.id ?? null)
                }
                emptyText="No presets yet. Fill in the answers on the next step and save them as a preset."
              />
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("choice")}>
                Back
              </Button>
              <Button
                variant="solid"
                disabled={!selectedPresetId}
                loading={isWorking}
                onClick={startFromPreset}
              >
                Activate Keyword Agent
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "criteria" && (
          <>
            <DialogHeader>
              <Stack gap="1">
                <DialogTitle>Campaign criteria</DialogTitle>
                <DialogDescription>
                  {brand.productName} evaluates every search term based on these
                  answers.
                </DialogDescription>
              </Stack>
            </DialogHeader>
            <DialogBody>
              <Stack gap="4">
                <CampaignCriteriaFields control={control} errors={errors} />
                <Stack gap="2">
                  <Checkbox
                    checked={saveAsPreset}
                    onCheckedChange={(details) =>
                      setSaveAsPreset(!!details.checked)
                    }
                  >
                    Save these answers as a preset
                  </Checkbox>
                  {saveAsPreset && (
                    <Field>
                      <Input
                        size="md"
                        placeholder="Name this preset"
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                      />
                    </Field>
                  )}
                </Stack>
              </Stack>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setStep("choice")}>
                Back
              </Button>
              <Button
                variant="solid"
                loading={isWorking}
                onClick={startFromAnswers}
              >
                Activate Keyword Agent
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </DialogRoot>
  );
}
